import {
  Check,
  ChevronsUpDown,
  CircleCheckIcon,
  CircleDashedIcon,
  CircleXIcon,
  ClockIcon,
  type LucideIcon,
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import type { ReimbursalStatus } from "../api/use-expense-mutations";

interface StatusOption {
  value: ReimbursalStatus;
  label: string;
  icon: LucideIcon;
  triggerClass: string;
  iconClass: string;
}

/** Port of ui/select/ReimbursalStatusSelect.vue. */
const STATUS_OPTIONS: StatusOption[] = [
  {
    value: "PENDING",
    label: "En attente",
    icon: ClockIcon,
    triggerClass: "bg-slate-50 text-slate-700 ring-slate-600/20",
    iconClass: "text-slate-500",
  },
  {
    value: "REIMBURSED",
    label: "Remboursé",
    icon: CircleCheckIcon,
    triggerClass: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
    iconClass: "text-emerald-600",
  },
  {
    value: "PARTIAL",
    label: "Partiel",
    icon: CircleDashedIcon,
    triggerClass: "bg-amber-50 text-amber-700 ring-amber-600/20",
    iconClass: "text-amber-600",
  },
  {
    value: "REJECTED",
    label: "Rejeté",
    icon: CircleXIcon,
    triggerClass: "bg-rose-50 text-rose-700 ring-rose-600/20",
    iconClass: "text-rose-600",
  },
];

interface ReimbursalStatusSelectProps {
  value: ReimbursalStatus;
  onChange: (status: ReimbursalStatus) => void;
  disabled?: boolean;
}

export function ReimbursalStatusSelect({ value, onChange, disabled }: ReimbursalStatusSelectProps) {
  const selected = STATUS_OPTIONS.find((option) => option.value === value) ?? STATUS_OPTIONS[0];
  const SelectedIcon = selected.icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={disabled}
        className={cn(
          "relative flex items-center gap-1.5 rounded-md py-1 pl-2 pr-6 text-xs font-medium ring-1 ring-inset disabled:cursor-not-allowed disabled:opacity-50",
          selected.triggerClass,
        )}
      >
        <SelectedIcon className={cn("h-3 w-3", selected.iconClass)} />
        <span className="truncate">{selected.label}</span>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1">
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" aria-hidden="true" />
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-36">
        {STATUS_OPTIONS.map((option) => {
          const Icon = option.icon;
          return (
            <DropdownMenuItem key={option.value} onClick={() => onChange(option.value)}>
              <Icon className={cn("h-3.5 w-3.5", option.iconClass)} />
              <span className={cn("truncate text-xs", option.value === value && "font-semibold")}>
                {option.label}
              </span>
              {option.value === value ? <Check className="ml-auto h-3.5 w-3.5 text-blue-600" /> : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
